import { useMemo } from 'react';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useRouter } from 'next/router';
import { useObserver } from 'mobx-react-lite';
import {
  ConnectionStatus,
  GqlListRetailerPaymentTypesStatusQuery,
  StatusTypes,
  useListRetailerPaymentTypesStatusQuery,
} from 'types/graphql';
import useDispensaryPaymentMethods from 'shared/hooks/use-dispensary-payment-methods';
import useCart from 'src/hooks/use-cart';
import useUI from 'src/hooks/use-ui';
import useUser from 'shared/hooks/use-user';
import useDispensary from 'src/dispensary/hooks/use-dispensary';
import { ApolloQueryResult } from 'apollo-client';
import { LOG_NAMESPACES, relinkBankAccount } from 'src/payments/constants';
import { InterfaceVariants } from 'src/constants/interface-variants';
import { getLogger } from 'src/utils/datadog-logger';

import type { EnrollmentBankAccount } from 'src/state/dutchiePay-enrollment';

const logger = getLogger(LOG_NAMESPACES.PBB_ENROLLMENT);

export type WhiteLabelConfig = {
  useWhiteLabel: boolean;
  brandedDutchiePayName: string | undefined;
  brandedDutchiePayLogoLight: string | undefined;
  brandedDutchiePayLogoDark: string | undefined;
};

export type UseDutchiePayUtils = {
  aggregator: string | undefined;
  allowDutchiePayEnrollment: boolean;
  bankAccounts: EnrollmentBankAccount[];
  connectionStatus: string | undefined;
  enrollmentStatusPending: boolean;
  hasEnrollmentData: boolean;
  isDutchiePayEnabledForDispo: boolean;
  isEnrolledDutchiePay: boolean;
  paymentMethodId: string | undefined;
  refetchRetailerPaymentData: () => Promise<ApolloQueryResult<GqlListRetailerPaymentTypesStatusQuery>>;
  shouldIncludePaymentMethodId: boolean;
  shouldShowBankAccountTab: boolean;
  whiteLabelConfig: WhiteLabelConfig;
  disablePBBSignupForCuraleafPlus: boolean;
  activeBankAccount?: EnrollmentBankAccount;
  needsRelinkBankAccount: boolean;
};

export const useDutchiePay = (): UseDutchiePayUtils => {
  // Hooks
  const User = useUser();
  const router = useRouter();
  const UI = useUI();
  const { variant, hideLogin, isPlus, lastDispensary } = UI;
  const Cart = useCart();
  const flags = useFlags();
  const enrollmentStatus = useObserver(() => User.payByBankEnrollmentStatus);
  const bankAccounts = useObserver(() => User.payByBankBankAccounts);
  const { dispensary: routeDispensary } = useDispensary();

  const { dispensary } = Cart.order;

  // Determine effective dispensary for context:
  // 1. Cart dispensary (highest priority - user has items from this dispensary)
  // 2. Route dispensary (user is on a dispensary page)
  // 3. Last dispensary (user recently viewed a dispensary, stored in UI state)
  const contextDispensary = dispensary || routeDispensary || lastDispensary;

  // Pass contextDispensary to useDispensaryPaymentMethods for embedded mode
  // (Dutchie Main ignores this arg and uses cart dispensary internally)
  const paymentOptions = useDispensaryPaymentMethods({ dispensary: contextDispensary });

  // Flags
  const internalSwitchesEnabled = flags['fintech.payment-processing.use-internal-feature-switches'];
  // Curaleaf is using a plus checkout within the context of their native mobile app which is preventing sso login due to the SSO login
  // not launching. They plan to move off of plus checkout and onto our white label mobile app but we need this flag in place until that occurs.
  // Once they move off of plus checkout, we can remove this variable and its references as they are only relevant to curaleaf plus.
  const disablePBBSignupForCuraleafPlus =
    flags['fintech.platform.disable-pbb-for-curaleaf-plus.rollout'] && isPlus && !!hideLogin;
  const { brandedDutchiePayLogoDark, brandedDutchiePayLogoLight, brandedDutchiePayName } = dispensary || {};
  const { plus, embedded } = InterfaceVariants;

  const hasWhiteLabelAssets =
    !!dispensary?.brandedDutchiePayName &&
    !!dispensary?.brandedDutchiePayLogoLight &&
    !!dispensary?.brandedDutchiePayLogoDark;

  const isWhiteLabeled = Boolean(
    dispensary?.brandedDutchiePayEnabled && hasWhiteLabelAssets && [embedded, plus].includes(variant)
  );

  const whiteLabelConfig = {
    useWhiteLabel: isWhiteLabeled,
    brandedDutchiePayName: isWhiteLabeled ? brandedDutchiePayName : '',
    brandedDutchiePayLogoLight: isWhiteLabeled ? brandedDutchiePayLogoLight : '',
    brandedDutchiePayLogoDark: isWhiteLabeled ? brandedDutchiePayLogoDark : '',
  };

  // Use context dispensary ID for retailer payment query
  const effectiveDispensaryId = contextDispensary?.id;

  const {
    data: retailerPaymentData,
    error: retailerPaymentError,
    refetch: refetchRetailerPaymentData,
  } = useListRetailerPaymentTypesStatusQuery({
    variables: { ecommLocationId: effectiveDispensaryId },
    skip: !internalSwitchesEnabled || !effectiveDispensaryId,
  });

  if (retailerPaymentError) {
    logger.error('Error fetching retailer payment data', {
      error: retailerPaymentError,
      userId: User?.id ?? 'unknown',
      dispensaryId: effectiveDispensaryId ?? 'unknown',
    });
  }

  const retailerIsEnabled = useMemo(
    () =>
      (retailerPaymentData?.listRetailerPaymentTypesStatus?.paymentTypes ?? []).some(
        (pmtType) => pmtType?.type === 'PAYMENT_TYPE_DUTCHIE_PAY' && pmtType.enabled === true
      ),
    [retailerPaymentData]
  );

  const activeBankAccount = useMemo(() => bankAccounts?.find((acct) => acct?.status === 'active'), [bankAccounts]);

  const paymentMethodId = activeBankAccount?.id;

  const isEnrolledDutchiePay = enrollmentStatus === StatusTypes.statusEnrolled && !!activeBankAccount;

  const enrollmentStatusPending = enrollmentStatus === StatusTypes.statusAccountPending;

  const aggregator = activeBankAccount?.aggregator;

  // Current dispo settings
  const payByBankActive = paymentOptions?.some(({ value, disabled }) => value === 'dutchiePay' && !disabled);

  const isDutchiePayEnabledForDispo = useMemo(
    () =>
      internalSwitchesEnabled
        ? retailerIsEnabled && !!payByBankActive && !disablePBBSignupForCuraleafPlus
        : !!payByBankActive && !disablePBBSignupForCuraleafPlus,
    [internalSwitchesEnabled, retailerIsEnabled, payByBankActive, disablePBBSignupForCuraleafPlus]
  );

  const allowDutchiePayEnrollment = isDutchiePayEnabledForDispo && !isEnrolledDutchiePay;
  const connectionStatus = activeBankAccount?.connectionStatus;

  const needsRelinkBankAccount = useMemo(
    () =>
      connectionStatus === ConnectionStatus.connectionStatusPendingExpiration ||
      connectionStatus === ConnectionStatus.connectionStatusError,
    [connectionStatus]
  );

  const relinkBankAccountEnabled = router.query.step === relinkBankAccount;
  const shouldIncludePaymentMethodId = Boolean(paymentMethodId && relinkBankAccountEnabled && needsRelinkBankAccount);

  // Show Bank Account tab based on context:
  // - If any dispensary context exists (cart, route, or last viewed): use isDutchiePayEnabledForDispo
  // - If no dispensary context (pure Dutchie Main browsing): always show
  const hasDispensaryContext = !!contextDispensary;
  const shouldShowBankAccountTab = hasDispensaryContext ? isDutchiePayEnabledForDispo : true;

  return {
    activeBankAccount,
    aggregator,
    allowDutchiePayEnrollment,
    bankAccounts,
    connectionStatus,
    enrollmentStatusPending,
    hasEnrollmentData: !!enrollmentStatus && !!activeBankAccount,
    isDutchiePayEnabledForDispo,
    isEnrolledDutchiePay,
    paymentMethodId,
    refetchRetailerPaymentData,
    shouldIncludePaymentMethodId,
    shouldShowBankAccountTab,
    whiteLabelConfig,
    disablePBBSignupForCuraleafPlus,
    needsRelinkBankAccount,
  };
};
